import { z } from "zod";
import { Taxonomy } from "@common/categories";

export const categorySchema = z.object({
    id: z.string(),
    label: z.string(),
    isBusiness: z.boolean(),
    description: z.string().nullable(),
    workspaceId: z.string().nullable(),
    taxonomy: z.nativeEnum(Taxonomy),
    parentCategoryId: z.string().nullable(),
});

export type CategorySchema = z.infer<typeof categorySchema>;

export const createCategoryBodySchema = z.object({
    label: z.string().min(1).describe("Display label for the custom category."),
    description: z
        .string()
        .describe("Optional custom category description.")
        .optional()
        .nullable(),
    parentCategoryId: z
        .string()
        .uuid()
        .describe("Parent category UUIDv7 from categories_query."),
});

export const categoryGlMappingOverrideSchema = z.object({
    entityId: z.number().int().positive(),
    inAccountId: z.string().uuid().nullable(),
    outAccountId: z.string().uuid().nullable(),
});

export const categoryGlMappingOverridesSchema = z
    .array(categoryGlMappingOverrideSchema)
    .max(100)
    .refine(
        (overrides) =>
            new Set(overrides.map((override) => override.entityId)).size ===
            overrides.length,
        { message: "Each entity may appear at most once in overrides." },
    );

export const createCategoryWithGlMappingsBodySchema = z.object({
    parentCategoryId: z
        .string()
        .uuid()
        .describe("Parent top-level business category UUID."),
    label: z.string().min(1).describe("Display label for the custom category."),
    description: z
        .string()
        .describe("Optional custom category description.")
        .optional()
        .nullable(),
    overrides: categoryGlMappingOverridesSchema.describe(
        "Per-entity diverging account overrides; absent entities use defaults.",
    ),
});

export type CategoryGlMappingOverride = z.infer<
    typeof categoryGlMappingOverrideSchema
>;
export type CreateCategoryWithGlMappingsBody = z.infer<
    typeof createCategoryWithGlMappingsBodySchema
>;

export const updateCategoryGlMappingsBodySchema = z.object({
    overrides: categoryGlMappingOverridesSchema.describe(
        "Desired per-entity account overrides; the server diffs them against the subcategory's existing rules.",
    ),
    applyToExistingTransactions: z
        .boolean()
        .describe(
            "When true, re-applies created or updated mapping rules to existing transactions after commit.",
        ),
});

export type UpdateCategoryGlMappingsBody = z.infer<
    typeof updateCategoryGlMappingsBodySchema
>;

export const updateCategoryBodySchema = z.object({
    label: z
        .string()
        .min(1)
        .describe("Updated display label for the category."),
    description: z
        .string()
        .describe("Updated category description, or null to clear it.")
        .optional()
        .nullable(),
});

export const categorySearchQuerySchema = z.object({
    search: z.string().trim().min(1).max(100),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z
        .string()
        .regex(/^(0|[1-9]\d*)$/)
        .optional(),
});

export const categorySearchRowSchema = z.object({
    id: z.string().uuid(),
    label: z.string(),
    path: z.string(),
    parentCategoryId: z.string().uuid().nullable(),
    parentLabel: z.string().nullable(),
    taxonomy: z.nativeEnum(Taxonomy),
    scope: z.enum(["standard", "custom"]),
    workspaceId: z.string().uuid().nullable(),
});

export const categorySearchResponseSchema = z.object({
    rows: z.array(categorySearchRowSchema),
    total: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
});

export type CategorySearchQuery = z.infer<typeof categorySearchQuerySchema>;
export type CategorySearchResponse = z.infer<
    typeof categorySearchResponseSchema
>;
